/**
 * Two-step e-mail-OTP login page. Step 1 = email, step 2 = 6-digit code.
 *
 * UX-features:
 * - **Cooldown op "Code opnieuw versturen"**: na het aanvragen van een
 *   code is de knop 30 seconden disabled met een aftelteller, zodat de
 *   gebruiker niet kan spammen (de worker begrenst ook: 5 codes per
 *   adres per uur).
 * - **Rate-limit-feedback**: bij een 429-response van /auth/start of
 *   /auth/verify lezen we de `Retry-After`-header en tonen 'n
 *   aftelteller "Probeer opnieuw over 0:42". Submit is disabled zolang
 *   de cooldown loopt.
 *
 * Cookie-flow: na succesvolle verify krijgt de browser een httpOnly
 * `ae_sess`-cookie + niet-httpOnly `ae_sess_exp` met de unix-timestamp
 * van de vervaldatum. De banner-component (zie `SessionExpiringBanner`)
 * leest die exp om te bepalen of de gebruiker een verlenging-prompt
 * moet zien.
 */
import { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate, useSearchParams } from "react-router-dom";
import { WEB_VERSION_LABEL } from "../buildInfo";
import { ProductMark } from "./ProductMark";

type Step = "email" | "code";

const RESEND_COOLDOWN_S = 30;

async function postJson(path: string, body: unknown): Promise<Response> {
  return fetch(path, {
    method: "POST",
    credentials: "include",
    headers: { "content-type": "application/json", accept: "application/json" },
    body: JSON.stringify(body),
  });
}

/** Format remaining seconds as `m:ss`. */
function formatSeconds(s: number): string {
  const m = Math.floor(s / 60);
  const sec = s % 60;
  return `${m}:${sec.toString().padStart(2, "0")}`;
}

/**
 * Parse the Retry-After header. RFC 7231 allows two formats: a number
 * of seconds OR an HTTP-date. We support both — onbekende formats
 * vallen terug op 60 seconden zodat de UI nooit oneindig wacht.
 */
function parseRetryAfter(headerValue: string | null): number {
  if (!headerValue) return 0;
  const asNumber = Number.parseInt(headerValue, 10);
  if (Number.isFinite(asNumber) && asNumber >= 0) return asNumber;
  const asDate = Date.parse(headerValue);
  if (Number.isFinite(asDate)) {
    const diffMs = asDate - Date.now();
    return diffMs > 0 ? Math.ceil(diffMs / 1000) : 0;
  }
  return 60;
}

/** De taalkeuze op de inlogpagina, per browser onthouden. Na het inloggen
 *  neemt de accountvoorkeur (`ui_language`) het over. */
const LOGIN_LANGUAGE_KEY = "ae.loginLanguage";

function readLoginLanguage(): "nl" | "en" | null {
  try {
    const v = localStorage.getItem(LOGIN_LANGUAGE_KEY);
    return v === "nl" || v === "en" ? v : null;
  } catch {
    return null;
  }
}

function writeLoginLanguage(lng: "nl" | "en"): void {
  try {
    localStorage.setItem(LOGIN_LANGUAGE_KEY, lng);
  } catch {
    // Geen opslag (privévenster, geblokkeerd): de keuze geldt dan alleen nu.
  }
}

export function LoginPage() {
  const { t, i18n } = useTranslation();
  const [step, setStep] = useState<Step>("email");
  const [email, setEmail] = useState("");
  const [code, setCode] = useState("");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const [info, setInfo] = useState<string | null>(null);
  // Seconden tot de "Stuur opnieuw"-knop weer mag — wordt gezet na
  // elke geslaagde /auth/start-call.
  const [resendIn, setResendIn] = useState(0);
  // Seconden tot de gebruiker WEL weer mag indienen. Gezet bij een
  // 429-response. Geldt voor zowel email-step als code-step; in beide
  // gevallen is de submit-knop disabled tijdens de cooldown.
  const [rateLimitedFor, setRateLimitedFor] = useState(0);
  const nav = useNavigate();
  const [params] = useSearchParams();
  const returnTo = params.get("next") || "/";
  const codeInputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (step === "code") codeInputRef.current?.focus();
  }, [step]);

  const chooseLanguage = (lng: "nl" | "en") => {
    void i18n.changeLanguage(lng);
    document.documentElement.lang = lng;
  };

  useEffect(() => {
    const saved = readLoginLanguage();
    if (saved && saved !== i18n.language) chooseLanguage(saved);
    // Alleen bij het openen van de pagina.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Een verse installatie heeft nog niemand die kan inloggen: dan eerst
  // inrichten. `/setup/status` is 404 zodra dat gebeurd is.
  useEffect(() => {
    let cancelled = false;
    fetch("/api/v1/setup/status", { credentials: "include" })
      .then((r) => {
        if (!cancelled && r.status === 200) nav("/setup", { replace: true });
      })
      .catch(() => {});
    return () => {
      cancelled = true;
    };
  }, [nav]);

  // Tick beide aftellers één keer per seconde. We gebruiken één
  // setInterval voor allebei zodat ze synchroon lopen.
  useEffect(() => {
    if (resendIn === 0 && rateLimitedFor === 0) return;
    const id = window.setInterval(() => {
      setResendIn((s) => Math.max(0, s - 1));
      setRateLimitedFor((s) => Math.max(0, s - 1));
    }, 1000);
    return () => window.clearInterval(id);
  }, [resendIn, rateLimitedFor]);

  /**
   * Vraag een (eerste of nieuwe) code aan voor de huidige `email`.
   * Geconsolideerd in één functie zodat zowel het eerste-formulier
   * als de "Stuur opnieuw"-knop dezelfde flow gebruiken — incl.
   * 429-handling.
   */
  const requestCode = async (): Promise<boolean> => {
    setErr(null);
    setBusy(true);
    try {
      const res = await postJson("/api/v1/auth/start", { email: email.trim() });
      if (res.status === 429) {
        const wait = parseRetryAfter(res.headers.get("retry-after"));
        setRateLimitedFor(wait || 60);
        setErr(t("auth.login.tooManyAttempts", { time: formatSeconds(wait || 60) }));
        return false;
      }
      if (res.status === 503) {
        setErr(t("auth.login.mailNotConfigured"));
        return false;
      }
      if (!res.ok) {
        setErr(t("auth.login.requestFailed"));
        return false;
      }
      // De eerste keer zegt de code-stap zelf al waar de code heen ging;
      // alleen een nieuwe code krijgt een eigen melding.
      if (step === "code") setInfo(t("auth.login.codeResent"));
      setResendIn(RESEND_COOLDOWN_S);
      return true;
    } catch {
      setErr(t("auth.login.networkError"));
      return false;
    } finally {
      setBusy(false);
    }
  };

  const onSubmitEmail = async (e: React.FormEvent) => {
    e.preventDefault();
    const ok = await requestCode();
    if (ok) setStep("code");
  };

  const onResend = async () => {
    if (resendIn > 0 || rateLimitedFor > 0) return;
    await requestCode();
  };

  const onSubmitCode = async (e: React.FormEvent) => {
    e.preventDefault();
    setErr(null);
    setBusy(true);
    try {
      const res = await postJson("/api/v1/auth/verify", {
        email: email.trim(),
        code: code.trim(),
      });
      if (res.status === 429) {
        const wait = parseRetryAfter(res.headers.get("retry-after"));
        setRateLimitedFor(wait || 60);
        setErr(t("auth.login.tooManyAttempts", { time: formatSeconds(wait || 60) }));
        return;
      }
      if (!res.ok) {
        setErr(t("auth.login.codeInvalid"));
        return;
      }
      // Cookie is set; navigate to the requested page. /authorize (koppelen
      // van de MCP-connector) is geen pagina van de web-app maar van de
      // worker: die vraagt een echte paginalading, anders toont de router 'm
      // niet.
      if (returnTo.startsWith("/authorize")) {
        window.location.assign(returnTo);
        return;
      }
      nav(returnTo, { replace: true });
    } catch {
      setErr(t("auth.login.networkError"));
    } finally {
      setBusy(false);
    }
  };

  const submitDisabled = busy || rateLimitedFor > 0;

  const fieldClass =
    "mt-2 w-full rounded-md border border-(--ld-control) bg-(--ld-surface) px-3 text-(--ld-text) transition-colors focus:border-(--ld-accent) focus:outline-none focus:ring-1 focus:ring-(--ld-accent) focus-visible:outline-none disabled:opacity-70";
  const primaryClass =
    "mt-5 h-10 w-full rounded-md bg-(--ld-primary) px-4 font-medium text-(--ld-primary-ink) text-sm transition-colors hover:bg-(--ld-primary-hover) active:translate-y-px disabled:translate-y-0 disabled:bg-(--ld-disabled-bg) disabled:text-(--ld-disabled-ink)";
  const textButtonClass =
    "rounded-sm font-medium text-(--ld-accent) underline-offset-2 hover:underline disabled:cursor-not-allowed disabled:font-normal disabled:text-(--ld-muted) disabled:no-underline";

  const submitLabel = (idle: string) =>
    busy
      ? t("auth.login.busy")
      : rateLimitedFor > 0
        ? t("auth.login.wait", { time: formatSeconds(rateLimitedFor) })
        : idle;

  return (
    <div className="ledger flex min-h-screen flex-col bg-(--ld-ground)">
      <main className="flex flex-1 items-center justify-center px-4 py-12">
        <div className="w-full max-w-[400px]">
          <div className="flex items-center gap-3">
            <ProductMark />
            <h1 className="font-semibold text-2xl tracking-[-0.01em]">{t("app.title")}</h1>
          </div>
          <p className="mt-3 mb-8 text-pretty text-(--ld-muted) text-[15px] leading-6">
            {t("auth.login.lead")}
          </p>

          <section
            aria-labelledby="login-step-heading"
            className="rounded-lg border border-(--ld-border) bg-(--ld-surface) p-6"
          >
            {step === "email" ? (
              <form onSubmit={onSubmitEmail}>
                <h2 id="login-step-heading" className="mb-5 font-medium text-base">
                  {t("auth.login.emailHeading")}
                </h2>
                <label htmlFor="login-email" className="block font-medium text-sm">
                  {t("auth.login.emailLabel")}
                </label>
                <input
                  id="login-email"
                  type="email"
                  required
                  autoComplete="email"
                  aria-describedby="login-email-hint"
                  className={`${fieldClass} h-10 text-[15px]`}
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  disabled={busy}
                />
                <p id="login-email-hint" className="mt-2 text-(--ld-muted) text-[13px]">
                  {t("auth.login.emailHint")}
                </p>
                <button type="submit" disabled={submitDisabled || !email} className={primaryClass}>
                  {submitLabel(t("auth.login.sendCode"))}
                </button>
              </form>
            ) : (
              <form onSubmit={onSubmitCode}>
                <h2 id="login-step-heading" className="mb-2 font-medium text-base">
                  {t("auth.login.codeHeading")}
                </h2>
                <p className="text-sm leading-6">
                  {t("auth.login.codeSentTo")}{" "}
                  <strong className="break-all font-medium">{email}</strong>.{" "}
                  <button
                    type="button"
                    className={textButtonClass}
                    onClick={() => {
                      setStep("email");
                      setCode("");
                      setInfo(null);
                      setErr(null);
                    }}
                  >
                    {t("auth.login.otherAddress")}
                  </button>
                </p>
                <p className="mt-1 mb-5 text-(--ld-muted) text-[13px]">
                  {t("auth.login.codeDelay")}
                </p>
                <label htmlFor="login-code" className="block font-medium text-sm">
                  {t("auth.login.codeLabel")}
                </label>
                <input
                  id="login-code"
                  ref={codeInputRef}
                  type="text"
                  inputMode="numeric"
                  autoComplete="one-time-code"
                  pattern="\d{6}"
                  maxLength={6}
                  required
                  className={`${fieldClass} h-12 text-center font-medium text-[22px] tracking-[0.4em] tabular-nums`}
                  value={code}
                  onChange={(e) => setCode(e.target.value.replace(/\D/g, "").slice(0, 6))}
                  disabled={busy}
                />
                <button
                  type="submit"
                  disabled={submitDisabled || code.length !== 6}
                  className={primaryClass}
                >
                  {submitLabel(t("auth.login.signIn"))}
                </button>
                <div className="mt-4 text-center text-sm">
                  <button
                    type="button"
                    className={textButtonClass}
                    onClick={() => void onResend()}
                    disabled={busy || resendIn > 0 || rateLimitedFor > 0}
                  >
                    {resendIn > 0
                      ? t("auth.login.resendIn", { seconds: resendIn })
                      : t("auth.login.resend")}
                  </button>
                </div>
              </form>
            )}

            <div aria-live="polite">
              {info && <p className="mt-4 text-(--ld-positive) text-sm">{info}</p>}
              {err && (
                <p
                  role="alert"
                  className="mt-4 rounded-md bg-(--ld-negative-bg) px-3 py-2 text-(--ld-negative-ink) text-sm"
                >
                  {err}
                </p>
              )}
            </div>
          </section>
        </div>
      </main>

      <footer className="flex items-center justify-between gap-4 px-6 py-4 text-(--ld-muted) text-xs">
        <span>{t("auth.login.version", { version: WEB_VERSION_LABEL })}</span>
        <fieldset className="flex items-center gap-1">
          <legend className="sr-only">{t("auth.login.language")}</legend>
          {(["nl", "en"] as const).map((lng) => (
            <button
              key={lng}
              type="button"
              aria-pressed={i18n.language === lng}
              onClick={() => {
                chooseLanguage(lng);
                writeLoginLanguage(lng);
              }}
              className={
                i18n.language === lng
                  ? "rounded-md bg-(--ld-surface) px-2 py-1 font-medium text-(--ld-text)"
                  : "rounded-md px-2 py-1 hover:text-(--ld-text)"
              }
            >
              {lng.toUpperCase()}
            </button>
          ))}
        </fieldset>
      </footer>
    </div>
  );
}
